/**
 * 再生中トラックのグローバル通知。
 *
 * フローティングプレイヤーバー（AudioPlayerBar）が持つ <audio> の再生を、
 * 一覧に並んだサムネイル側が知るための仕組み。バーは画面下に固定された別の
 * コンポーネントツリーにあり props で繋がっていないため、playbackArbiter と
 * 同じくモジュールシングルトンにする。
 *
 * キーは音源 URL。バーとサムネイルは同じ URL を受け取るので、ページ側に追加の
 * 配線を入れずに突き合わせられる。
 */

import { useCallback, useEffect, useState } from 'react';

import { SpectrumFrame } from './spectrumFeatures';

export interface ActiveTrack {
  /** 音源 URL。サムネイル側は自分の src と一致するかで判定する */
  key: string;
  isPlaying: boolean;
  /** 再生位置（秒）。毎フレーム呼ばれるので関数で渡す */
  getCurrentTime: () => number;
  /**
   * 実スペクトルの現フレーム。AnalyserNode が繋がっていない（CORS 不可等）
   * ときは常に null を返す。サムネイル側は null なら peaks 由来の駆動に戻す。
   */
  readSpectrum: () => SpectrumFrame | null;
}

let activeTrack: ActiveTrack | null = null;
const listeners = new Set<() => void>();

const notify = (): void => {
  listeners.forEach(listener => listener());
};

/** 再生中トラックを差し替える（バー側が再生状態の変化ごとに呼ぶ） */
export function setActiveTrack(track: ActiveTrack): void {
  activeTrack = track;
  notify();
}

/** 自分が登録したトラックを取り下げる（別トラックに切り替わっていれば何もしない） */
export function clearActiveTrack(key: string): void {
  if (activeTrack?.key !== key) return;
  activeTrack = null;
  notify();
}

export function getActiveTrack(): ActiveTrack | null {
  return activeTrack;
}

export function subscribeActiveTrack(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * 指定キーがフローティングバーで再生中かを購読する。
 * getCurrentTime は毎フレーム呼ばれる想定なので、再生状態が変わるまで同一参照を保つ。
 */
export function useActiveTrackPlayback(key: string | null | undefined): {
  isPlaying: boolean;
  getCurrentTime: () => number;
  readSpectrum: () => SpectrumFrame | null;
} {
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (!key) {
      setIsPlaying(false);
      return;
    }
    const sync = () => {
      const track = getActiveTrack();
      setIsPlaying(track?.key === key && track.isPlaying);
    };
    sync();
    return subscribeActiveTrack(sync);
  }, [key]);

  const getCurrentTime = useCallback(() => {
    const track = getActiveTrack();
    if (!track || track.key !== key) return 0;
    return track.getCurrentTime();
  }, [key]);

  const readSpectrum = useCallback(() => {
    const track = getActiveTrack();
    if (!track || track.key !== key) return null;
    return track.readSpectrum();
  }, [key]);

  return { isPlaying, getCurrentTime, readSpectrum };
}
